// src/components/SurveyRefill/SurveyBillingContacts.js
// Formulario exclusivo para EMPRESAS_NO_NUEVAS_2026; reutiliza el endpoint y el callback existentes.
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Button, Box, Text, Stack, FormControl, FormLabel, FormErrorMessage,
  Input, Radio, RadioGroup, Checkbox, HStack, Alert, AlertIcon,
} from '@chakra-ui/react'

const requiredIds = ['FAC_NOMBRE','FAC_EMAIL','FAC_EMAIL_ADIC','FAC_TELEFONO','COB_MISMOS',
  'COB_NOMBRE','COB_EMAIL','COB_EMAIL_ADIC','COB_TELEFONO','CONFIRMACION']
const billingIds = ['FAC_NOMBRE','FAC_EMAIL','FAC_EMAIL_ADIC','FAC_TELEFONO']
const collectingIds = ['COB_NOMBRE','COB_EMAIL','COB_EMAIL_ADIC','COB_TELEFONO']
const labels = {
  FAC_NOMBRE:'Nombre del responsable o departamento',
  FAC_EMAIL:'Correo electrónico principal',
  FAC_EMAIL_ADIC:'Correo(s) electrónico(s) adicional(es), si corresponde',
  FAC_TELEFONO:'Teléfono (opcional)',
  COB_NOMBRE:'Nombre del responsable o departamento',
  COB_EMAIL:'Correo electrónico principal',
  COB_EMAIL_ADIC:'Correo(s) electrónico(s) adicional(es), si corresponde',
  COB_TELEFONO:'Teléfono (opcional)',
}
const emailValid = (s) => /^[^\s@;,]+@[^\s@;,]+\.[^\s@;,]+$/.test(s)
const emailsValid = (s) => !s.trim() || s.split(/[;,\n]+/).map(x=>x.trim()).filter(Boolean).every(emailValid)
function valid(values, ids) {
  const errors = {}
  ids.forEach(id => {
    const v = (values[id] || '').trim()
    if ((id.endsWith('_NOMBRE') || id.endsWith('_EMAIL')) && !v) errors[id]='Campo obligatorio'
    if (id.endsWith('_EMAIL') && v && !emailValid(v)) errors[id]='Correo electrónico inválido'
    if (id.endsWith('_EMAIL_ADIC') && !emailsValid(v)) errors[id]='Separa los correos con coma o punto y coma'
    if (id.endsWith('_TELEFONO') && v && !/^[+0-9()\s-]{6,25}$/.test(v)) errors[id]='Teléfono inválido'
  })
  return errors
}
function FieldGroup({ ids, answers, setField, errors }) {
  return <Stack spacing={4}>
    {ids.map(id => <FormControl key={id} isRequired={id.endsWith('_NOMBRE') || id.endsWith('_EMAIL')} isInvalid={!!errors[id]}>
      <FormLabel color="white">{labels[id]}</FormLabel>
      <Input value={answers[id] || ''} maxLength={id.endsWith('_EMAIL_ADIC') ? 1000 : 250}
        type={id.endsWith('_EMAIL') ? 'email' : 'text'}
        onChange={e => setField(id,e.target.value)} />
      <FormErrorMessage>{errors[id]}</FormErrorMessage>
    </FormControl>)}
  </Stack>
}
FieldGroup.propTypes = { ids:PropTypes.array.isRequired, answers:PropTypes.object.isRequired,
  setField:PropTypes.func.isRequired, errors:PropTypes.object.isRequired }

// Formulario incrustado: NO crea otro Modal ni otra portada.
export default function SurveyBillingContacts({ onClose, onSubmitAnswers, questions }) {
  const [step, setStep] = useState(1)
  const [answers, setAnswers] = useState({})
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const ids = new Set((questions || []).map(q => q.id))
  const ready = requiredIds.every(id => ids.has(id))
  const field = (id, value) => {
    setAnswers(prev => ({ ...prev, [id]: value }))
    setErrors(prev => ({ ...prev, [id]: undefined }))
  }
  const same = answers.COB_MISMOS === 'SI'
  const advance = async () => {
    if (step === 1) {
      const e = valid(answers, billingIds)
      setErrors(e)
      if (!Object.keys(e).length) setStep(2)
      return
    }
    if (step === 2) {
      if (!['SI','NO'].includes(answers.COB_MISMOS)) {
        setSaveError('Selecciona una respuesta')
        return
      }
      setSaveError('')
      setStep(same ? 4 : 3)
      return
    }
    if (step === 3) {
      const e = valid(answers, collectingIds)
      setErrors(e)
      if (!Object.keys(e).length) setStep(4)
      return
    }
    if (step !== 4 || answers.CONFIRMACION !== 'SI') return
    const e = { ...valid(answers, billingIds), ...(!same ? valid(answers, collectingIds) : {}) }
    if (Object.keys(e).length) { setErrors(e); setSaveError('Revisa los datos ingresados'); return }
    const payload = {}
    billingIds.forEach(id => { const v = (answers[id] || '').trim(); if (v) payload[id] = { ValorTexto: v } })
    payload.COB_MISMOS = answers.COB_MISMOS
    if (!same) collectingIds.forEach(id => {
      const v = (answers[id] || '').trim()
      if (v) payload[id] = { ValorTexto: v }
    })
    payload.CONFIRMACION = 'SI'
    try {
      setSaving(true)
      setSaveError('')
      await onSubmitAnswers(payload)
      setStep(5)
    } catch (err) {
      setSaveError(err?.message || 'No se pudo registrar la encuesta')
    } finally { setSaving(false) }
  }
  const back = () => { setSaveError(''); setErrors({}); setStep(v => v === 4 && same ? 2 : Math.max(1, v - 1)) }
  return (
    <Box bg="#2A1E63" color="white" px={{ base: 6, md: 10 }} py={8} minH="400px">
      {!ready && <Alert status="error"><AlertIcon />Definición incompleta: faltan campos de la encuesta.</Alert>}
      {ready && step === 1 && <Stack spacing={4}>
        <Text fontSize="xl" fontWeight="bold">1. Contactos para recepción de facturas</Text>
        <FieldGroup ids={billingIds} answers={answers} setField={field} errors={errors} />
      </Stack>}
      {ready && step === 2 && <Stack spacing={4}>
        <Text fontSize="xl" fontWeight="bold">2. Contactos para gestión de cobranza</Text>
        <Text>¿Los contactos para cobranza son los mismos que para recepción de facturas?</Text>
        <RadioGroup value={answers.COB_MISMOS || ''} onChange={v => { field('COB_MISMOS',v); setSaveError('') }}>
          <Stack><Radio value="SI">Sí, utilizar los mismos contactos.</Radio><Radio value="NO">No, son contactos diferentes.</Radio></Stack>
        </RadioGroup>
      </Stack>}
      {ready && step === 3 && <Stack spacing={4}>
        <Text fontSize="xl" fontWeight="bold">Contactos diferentes para cobranza</Text>
        <FieldGroup ids={collectingIds} answers={answers} setField={field} errors={errors} />
      </Stack>}
      {ready && step === 4 && <Stack spacing={4}>
        <Text fontSize="xl" fontWeight="bold">3. Confirmación</Text>
        <Checkbox isChecked={answers.CONFIRMACION === 'SI'} onChange={e => field('CONFIRMACION',e.target.checked ? 'SI' : '')}>
          Confirmo que la información proporcionada se encuentra actualizada.
        </Checkbox>
      </Stack>}
      {step === 5 && <Stack spacing={4} align="center" py={10}>
        <Text fontSize="2xl" fontWeight="bold">¡Gracias por tu tiempo y respuestas!</Text>
        <Text>Ahora puedes continuar utilizando la plataforma.</Text>
      </Stack>}
      {!!saveError && <Alert status="error" mt={4}><AlertIcon />{saveError}</Alert>}
      <HStack justify="space-between" mt={8}>
        {ready && step > 1 && step < 5 && <Button onClick={back} isDisabled={saving} bg="whiteAlpha.200" color="white">Atrás</Button>}
        {ready && step < 5 && <Button onClick={advance} isLoading={saving} isDisabled={step === 4 && answers.CONFIRMACION !== 'SI'} bg="#6B56C4" color="white" ml="auto">{step === 4 ? 'Enviar' : 'Siguiente'}</Button>}
        {step === 5 && <Button onClick={onClose} bg="#6B56C4" color="white" ml="auto">Cerrar</Button>}
      </HStack>
    </Box>
  )
}

SurveyBillingContacts.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmitAnswers: PropTypes.func.isRequired,
  questions: PropTypes.array.isRequired,
}
